import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// Every line the Secrets screen shows (ADR-023 §4), in English and Spanish, read through
// `useVaultCopy` (behavior/use-vault-copy.ts). Pure: no React, no I/O.

const EN = {
  screenTitle: "Secrets · {product}",
  navSecrets: "Secrets",
  breadcrumbHome: "Home",
  breadcrumbSettings: "Settings",
  title: "Secrets",
  description: "Keys your agents use without reading them. Each value is sent only to the hosts you name.",
  neverShownAgain: "Once saved, a value is never shown again, not even to you.",
  addSecret: "Add secret",
  drawerTitle: "Add a secret",
  drawerDescription: "Your agents get a placeholder. We swap in the value on the way to the hosts you allow.",
  name: "Name",
  namePlaceholder: "HUNTER_KEY",
  nameHint: "Letters, digits and underscores.",
  value: "Value",
  valueHint: "Saved encrypted. You will not see it again.",
  hosts: "Allowed hosts",
  hostsPlaceholder: "api.hunter.io",
  hostsHint: "One or more host names, separated by commas.",
  agents: "Agents allowed to use it",
  noAgentsYet: "You have no agents yet. Save the secret now and allow agents later by saving it again.",
  cancel: "Cancel",
  save: "Save secret",
  saving: "Saving",
  secretSaved: "Secret saved",
  secretSavedDescription: "Your agents use it as {placeholder}.",
  couldNotSave: "Could not save that secret",
  invalidName: "Use letters, digits and underscores, starting with a letter.",
  invalidValue: "Enter the value to keep.",
  invalidHosts: "Enter host names such as api.example.com, without https:// or a path.",
  deleteTitle: 'Delete "{name}"?',
  deleteFallbackTitle: "Delete this secret?",
  deleteDescription: "Agents that use its placeholder stop getting the value right away.",
  delete: "Delete",
  deleteLabel: "Delete {name}",
  secretDeleted: "Secret deleted",
  couldNotDelete: "Could not delete that secret",
  tableLabel: "Secrets",
  emptyTitle: "No secrets yet",
  emptyDescription: "Add a key an agent needs, such as an enrichment API key.",
  columnName: "Name",
  columnPlaceholder: "Placeholder",
  columnHosts: "Hosts",
  columnAgents: "Agents",
  columnActions: "Actions",
  noAgents: "No agent",
  unknownAgent: "Removed agent",
  kindLabel: "Kind of secret",
  kindApiKey: "API key",
  kindWebsiteLogin: "Website login",
  columnKind: "Kind",
  loginDrawerTitle: "Add a website login",
  loginDrawerDescription:
    "Your agents never see it. When an agent asks to sign in, we type it into this site's login form ourselves.",
  loginNameHint: "The name an agent signs in with, such as instagram.",
  loginNamePlaceholder: "instagram",
  origin: "Site address",
  originPlaceholder: "https://www.instagram.com",
  originHint: "The https address of the site, without a path. We type the login on this site only.",
  username: "Username or email",
  password: "Password",
  totpSeed: "Two-step code key",
  totpSeedHint: "Optional. The setup key your authenticator app shows, so we can type the code too.",
  saveLogin: "Save login",
  loginSaved: "Login saved",
  loginSavedDescription: "Your agents can sign in with it. They never see the password.",
  invalidOrigin: "Enter an https address such as https://www.instagram.com, without a path.",
  invalidUsername: "Enter the username or email you sign in with.",
  invalidPassword: "Enter the password.",
  invalidTotpSeed: "Enter the key as your authenticator app shows it, letters A to Z and digits 2 to 7.",
} as const;

export type VaultCatalog = typeof EN;

const ES: Translation<VaultCatalog> = {
  screenTitle: "Secretos · {product}",
  navSecrets: "Secretos",
  breadcrumbHome: "Inicio",
  breadcrumbSettings: "Ajustes",
  title: "Secretos",
  description: "Claves que tus agentes usan sin leerlas. Cada valor se envía solo a los hosts que indiques.",
  neverShownAgain: "Una vez guardado, un valor no se vuelve a mostrar, ni siquiera a ti.",
  addSecret: "Añadir secreto",
  drawerTitle: "Añadir un secreto",
  drawerDescription:
    "Tus agentes reciben un marcador. Ponemos el valor real camino de los hosts que permitas.",
  name: "Nombre",
  namePlaceholder: "HUNTER_KEY",
  nameHint: "Letras, números y guiones bajos.",
  value: "Valor",
  valueHint: "Se guarda cifrado. No lo volverás a ver.",
  hosts: "Hosts permitidos",
  hostsPlaceholder: "api.hunter.io",
  hostsHint: "Uno o más nombres de host, separados por comas.",
  agents: "Agentes que pueden usarlo",
  noAgentsYet:
    "Aún no tienes agentes. Guarda el secreto ahora y permite agentes más tarde guardándolo de nuevo.",
  cancel: "Cancelar",
  save: "Guardar secreto",
  saving: "Guardando",
  secretSaved: "Secreto guardado",
  secretSavedDescription: "Tus agentes lo usan como {placeholder}.",
  couldNotSave: "No pudimos guardar ese secreto",
  invalidName: "Usa letras, números y guiones bajos, empezando por una letra.",
  invalidValue: "Escribe el valor que quieres guardar.",
  invalidHosts: "Escribe nombres de host como api.example.com, sin https:// ni una ruta.",
  deleteTitle: '¿Eliminar "{name}"?',
  deleteFallbackTitle: "¿Eliminar este secreto?",
  deleteDescription: "Los agentes que usan su marcador dejan de recibir el valor de inmediato.",
  delete: "Eliminar",
  deleteLabel: "Eliminar {name}",
  secretDeleted: "Secreto eliminado",
  couldNotDelete: "No pudimos eliminar ese secreto",
  tableLabel: "Secretos",
  emptyTitle: "Aún no hay secretos",
  emptyDescription: "Añade una clave que un agente necesite, como la clave de una API de enriquecimiento.",
  columnName: "Nombre",
  columnPlaceholder: "Marcador",
  columnHosts: "Hosts",
  columnAgents: "Agentes",
  columnActions: "Acciones",
  noAgents: "Ningún agente",
  unknownAgent: "Agente eliminado",
  kindLabel: "Tipo de secreto",
  kindApiKey: "Clave de API",
  kindWebsiteLogin: "Acceso a un sitio",
  columnKind: "Tipo",
  loginDrawerTitle: "Añadir un acceso a un sitio",
  loginDrawerDescription:
    "Tus agentes nunca lo ven. Cuando un agente pide iniciar sesión, lo escribimos nosotros en el formulario de este sitio.",
  loginNameHint: "El nombre con el que un agente inicia sesión, como instagram.",
  loginNamePlaceholder: "instagram",
  origin: "Dirección del sitio",
  originPlaceholder: "https://www.instagram.com",
  originHint: "La dirección https del sitio, sin ruta. Escribimos el acceso solo en este sitio.",
  username: "Usuario o email",
  password: "Contraseña",
  totpSeed: "Clave del código en dos pasos",
  totpSeedHint:
    "Opcional. La clave de configuración que muestra tu app de autenticación, para escribir también el código.",
  saveLogin: "Guardar acceso",
  loginSaved: "Acceso guardado",
  loginSavedDescription: "Tus agentes pueden iniciar sesión con él. Nunca ven la contraseña.",
  invalidOrigin: "Escribe una dirección https como https://www.instagram.com, sin ruta.",
  invalidUsername: "Escribe el usuario o el email con el que inicias sesión.",
  invalidPassword: "Escribe la contraseña.",
  invalidTotpSeed:
    "Escribe la clave tal como la muestra tu app de autenticación, letras de la A a la Z y números del 2 al 7.",
};

export const VAULT_CATALOGS: Catalogs<VaultCatalog> = { en: EN, es: ES };
